import React, { useState, useEffect } from 'react';
import { ArrowPathIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../../../utils/api';
import { toast } from 'react-hot-toast';

const SyncButton = ({ onSyncSuccess, onSyncError, className = "", compact = false }) => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Fetch sync status on component mount
  useEffect(() => {
    fetchSyncStatus();
    const interval = setInterval(fetchSyncStatus, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Countdown timer for wait time
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/api/plaid/sync-status');
      setSyncStatus(response.data.syncStatus);
      
      // Set countdown if user needs to wait
      if (!response.data.syncStatus.canSync && response.data.syncStatus.waitMinutes > 0) {
        setCountdown(response.data.syncStatus.waitMinutes * 60);
      }
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  const handleSync = async () => {
    if (!syncStatus?.canSync) {
      toast.error('Sync not available right now. Please check the rate limits.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Batch sync all items in one request
      const batchResponse = await api.post('/api/plaid/sync-all', {}, {
        timeout: 120000,
        headers: {
          'X-Sync-Source': 'manual'
        }
      });
      const results = batchResponse.data.results || [];
      const syncResults = {};
      let hasErrors = false;
      let totalTransactionsAdded = 0;
      let totalTransactionsUpdated = 0;

      results.forEach(r => {
        syncResults[r.institutionName] = {
          success: r.success,
          transactionSync: r.transactionSync,
          rateLimitInfo: batchResponse.data.rateLimitInfo
        };
        if (!r.success) hasErrors = true;
        if (r.transactionSync?.success) {
          totalTransactionsAdded += r.transactionSync.added || 0;
          totalTransactionsUpdated += r.transactionSync.updated || 0;
        }
      });

      // Reconcile with Plaid items: if item status is good, mark connected
      try {
        const itemsRes = await api.get('/api/plaid/items');
        const items = itemsRes?.data || [];
        items.forEach(item => {
          const name = item.institutionName;
          if (!name) return;
          if (item.status === 'good') {
            syncResults[name] = { success: true };
          }
        });
      } catch (_) {}

      // Persist sync results so Wallet badges update immediately
      try {
        localStorage.setItem('plaidSyncStatus', JSON.stringify(syncResults));
        localStorage.setItem('lastPlaidSync', Date.now().toString());
      } catch (_) {}

      // Success message with transaction info
      if (!hasErrors) {
        let message = 'Accounts synced successfully';
        if (totalTransactionsAdded > 0 || totalTransactionsUpdated > 0) {
          message += ` • ${totalTransactionsAdded} new transactions, ${totalTransactionsUpdated} updated`;
        }
        toast.success(message, { duration: 6000 });
      } else {
        const successCount = Object.values(syncResults).filter(r => r.success).length;
        const failedInstitutions = Object.entries(syncResults)
          .filter(([_, result]) => !result.success)
          .map(([name, _]) => name);
        
        let message = `Synced ${successCount} connection(s).`;
        if (failedInstitutions.length > 0) {
          if (failedInstitutions.length <= 3) {
            message += ` ${failedInstitutions.join(', ')} need reconnection.`;
          } else {
            message += ` ${failedInstitutions.length} accounts need reconnection.`;
          }
        }
        toast(message, { duration: 8000 });
      }

      // Notify parent component so it can update its syncStatus immediately
      if (onSyncSuccess) onSyncSuccess(syncResults);

    } catch (error) {
      console.error('Failed to sync accounts:', error);
      toast.error('Failed to sync accounts');
      if (onSyncError) {
        onSyncError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatWaitTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const isDisabled = isLoading || !syncStatus?.canSync;

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={handleSync}
          disabled={isDisabled}
          className={`
            bg-gradient-to-br from-white to-purple-50 p-3 rounded-xl border border-purple-200 
            flex flex-col items-center justify-center hover:shadow-md transition-all duration-200 w-full
            ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${className}
          `}
        >
          <div className="p-1.5 rounded-full bg-purple-100 mb-1.5">
            {isLoading ? (
              <ArrowPathIcon className="h-5 w-5 text-purple-600 animate-spin" />
            ) : !syncStatus?.canSync ? (
              <ClockIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ArrowPathIcon className="h-5 w-5 text-purple-600" />
            )}
          </div>
          <span className={`text-xs font-medium text-center leading-tight ${isDisabled ? 'text-gray-400' : 'text-purple-700'}`}>
            {isLoading ? 'Syncing...' : 'Sync Accounts'}
          </span>
          {syncStatus && (
            <div className="mt-0.5 text-[10px] text-center">
              {syncStatus.canSync ? (
                <span className="text-green-600">{syncStatus.syncsRemaining}/{syncStatus.maxSyncsPerDay} syncs left today</span>
              ) : syncStatus.waitMinutes > 0 ? (
                <span className="text-amber-600">Wait {formatWaitTime(syncStatus.waitMinutes)}</span>
              ) : (
                <span className="text-red-600">Daily limit reached</span>
              )}
            </div>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={handleSync}
        disabled={isDisabled}
        className={`
          bg-gradient-to-br from-white to-purple-50 p-4 rounded-xl shadow-md border border-purple-200 
          flex flex-col items-center justify-center hover:shadow-lg transition-all duration-200
          ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'}
          ${className}
        `}
      >
        <div className="p-2 rounded-full bg-purple-100 mb-2">
          {isLoading ? (
            <ArrowPathIcon className="h-6 w-6 text-purple-600 animate-spin" />
          ) : !syncStatus?.canSync ? (
            <ClockIcon className="h-6 w-6 text-gray-400" />
          ) : (
            <ArrowPathIcon className="h-6 w-6 text-purple-600" />
          )}
        </div>
        
        <span className={`text-sm font-medium ${isDisabled ? 'text-gray-400' : 'text-purple-700'}`}>
          {isLoading ? 'Syncing...' : 'Sync Accounts'}
        </span>
        
        {/* Rate limit info */}
        {syncStatus && (
          <div className="mt-1 text-xs text-center">
            {syncStatus.canSync ? (
              <span className="text-green-600">
                {syncStatus.syncsRemaining}/{syncStatus.maxSyncsPerDay} syncs left today
              </span>
            ) : syncStatus.waitMinutes > 0 ? (
              <div className="text-amber-600">
                <div>Wait {formatWaitTime(syncStatus.waitMinutes)}</div>
                {countdown > 0 && (
                  <div className="text-xs">{formatCountdown(countdown)}</div>
                )}
              </div>
            ) : (
              <span className="text-red-600">
                Daily limit reached
              </span>
            )}
          </div>
        )}
      </button>

      {/* Tooltip with rate limit explanation */}
      {syncStatus && !syncStatus.canSync && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
          <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap">
            <div className="flex items-center space-x-1">
              <ExclamationTriangleIcon className="h-4 w-4" />
              <span>Rate limit protects bank connections</span>
            </div>
            <div className="text-xs opacity-75 mt-1">
              Max {syncStatus.maxSyncsPerDay} syncs/day, {syncStatus.minIntervalMinutes}min interval
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncButton;
